import type { CSSProperties, ReactNode } from "react";
import { Fragment, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";

import DemoBanner from "./DemoBanner";
import ErrorBoundary from "./ErrorBoundary";
import FreshnessBadge from "./FreshnessBadge";
import HelpButton from "./helpdesk/HelpButton";
import { isStaff, signOut, useAuth } from "../lib/auth";
import { useExecKpiAvailability } from "../lib/execKpiData";
import { helpDeskEnabled, helpDeskVisible } from "../lib/helpDesk/helpDesk.mjs";
import { buildNavSections, executiveTabId } from "../lib/navSections.mjs";
import { entityForTabRoute } from "../lib/tabFreshness.mjs";
import { track } from "../lib/telemetry";
import type { DashboardManifest, ManifestTab, Tier } from "../lib/manifest";
import { usePrefetchReports } from "../reports";

/**
 * Brand shell for the per-customer dashboard. Header carries the
 * Meridian wordmark, a customer pill (slug + tier), and a sign-out
 * action. Left sidebar lists every manifest tab with a lock icon for
 * tier-gated tabs.
 *
 * MER-159 sub-4 split the sidebar into DASHBOARD / PRO+ / ANALYSIS /
 * ACCOUNT. MER-688 (AJ 2026-09-14) strips the client nav to the
 * essentials, and MER-690 (AJ 2026-09-15) fixes the order to his list:
 *   - DASHBOARD PRO+ : one section, the same for every session. The
 *                      Executive board link first (MER-674, only when the
 *                      tenant has the workbook), then the everyday operator
 *                      tabs, then any former Pro+ tab a client may see, in
 *                      manifest order within each group. Lock icon when the
 *                      customer's tier doesn't cover a visible tab.
 *   - INTERNAL       : staff sessions only (impersonating or not). Insights
 *                      and Findings, then every manifest tab flagged
 *                      `staff_only` (Benchmarking, Financial Health, Sales
 *                      Pipeline, Data Quality). A staff user therefore sees
 *                      exactly the client's section plus this block.
 *   - ACCOUNT        : Settings + an external CTA to the upload portal +
 *                      Sign out. Unchanged.
 * The section math lives in lib/navSections.mjs so node:test covers it.
 *
 * MER-361: manifest tab `id` IS the customer-facing URL slug AND the
 * display label. No alias map. Templates carry the user-facing name
 * directly (Data Quality / Sales Pipeline / Executive Roll-Up /
 * Safety & Fleet).
 */

// MER-528: below this width the fixed 240px sidebar stole too much of a 14"
// laptop's horizontal space and the content tabs overflowed. The sidebar
// collapses into a hamburger-toggled off-canvas drawer below the breakpoint
// instead of holding a fixed column.
const SIDEBAR_BREAKPOINT = "(max-width: 1100px)";

/**
 * MER-528: matchMedia-backed responsive flag. The shell is styled inline (no
 * CSS file), so the collapse decision is read in JS rather than via a media
 * query. SSR / jsdom-safe: lazily seeds from matchMedia when present, else
 * false, and never throws when matchMedia is unavailable.
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

const TIER_RANK: Record<Tier, number> = {
  essential: 1,
  growth: 2,
  professional: 3,
  enterprise: 4,
};

function tabIsUnlocked(tab: ManifestTab, customerTier: Tier | undefined): boolean {
  if (!tab.tier || !customerTier) return true;
  return TIER_RANK[customerTier] >= TIER_RANK[tab.tier];
}

function displayLabel(tab: ManifestTab): string {
  return tab.label ?? tab.id ?? "";
}

/** `/<slug>/<segment>/...` -> the decoded segment, or undefined. */
function routeTabSegment(pathname: string, slug: string | undefined): string | undefined {
  if (!slug) return undefined;
  const parts = pathname.split("/").filter(Boolean);
  const at = parts.indexOf(slug);
  const raw = at >= 0 ? parts[at + 1] : undefined;
  if (!raw) return undefined;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export interface DashboardShellProps {
  manifest: DashboardManifest;
  children: ReactNode;
}

const SECTION_HEADER_STYLE: CSSProperties = {
  padding: "8px 16px",
  fontSize: 10,
  letterSpacing: 1,
  textTransform: "uppercase",
  color: "var(--text-muted)",
  fontWeight: 700,
};

// MER-182: external links to upload.meridiananalytics.io use this
// flat style instead of navLinkStyle so they never carry an active
// underline (no React Router match is possible for an external URL).
const externalNavLinkStyle: CSSProperties = {
  padding: "9px 16px",
  fontSize: 13,
  color: "var(--text)",
  fontWeight: 400,
  background: "transparent",
  borderLeft: "3px solid transparent",
  textDecoration: "none",
  display: "grid",
  gridTemplateColumns: "1fr auto",
  alignItems: "center",
  gap: 8,
};

function navLinkStyle({ isActive }: { isActive: boolean }): CSSProperties {
  return {
    padding: "9px 16px",
    fontSize: 13,
    color: "var(--text)",
    fontWeight: isActive ? 600 : 400,
    background: isActive
      ? "linear-gradient(90deg, rgba(38,168,159,0.10) 0%, transparent 100%)"
      : "transparent",
    borderLeft: isActive ? "3px solid var(--brand-teal)" : "3px solid transparent",
    textDecoration: "none",
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: 8,
  };
}

function ManifestTabLink(props: {
  tab: ManifestTab;
  slug: string | undefined;
  customerTier: Tier | undefined;
}) {
  const { tab, slug, customerTier } = props;
  const tabId = tab.id ?? tab.label ?? "";
  const unlocked = tabIsUnlocked(tab, customerTier);
  return (
    <NavLink
      to={`/${slug}/${tabId}`}
      style={navLinkStyle}
      onClick={() => track("tab_change", { tab_id: tabId, tab_name: displayLabel(tab) })}
    >
      <span>{displayLabel(tab)}</span>
      {/* navLinkStyle is a two-column grid; badge and lock are mutually
          exclusive (a locked tab shows the lock, not NEW) so the second
          column never double-books. */}
      {tab.badge && unlocked ? (
        <span
          aria-label="tab-badge"
          style={{
            background: "var(--brand-teal, #26a89f)",
            color: "#fff",
            fontSize: 8.5,
            fontWeight: 700,
            borderRadius: 99,
            padding: "1.5px 6px",
            letterSpacing: 0.4,
            textTransform: "uppercase",
            marginLeft: "auto",
          }}
        >
          {tab.badge}
        </span>
      ) : null}
      {!unlocked ? (
        <span
          title={`Requires ${tab.tier} tier`}
          aria-label="tier-locked"
          style={{ fontSize: 11, color: "var(--text-muted)" }}
        >
          🔒
        </span>
      ) : null}
    </NavLink>
  );
}

export default function DashboardShell(props: DashboardShellProps) {
  const { manifest, children } = props;
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // MER-528: drawer mode below the breakpoint; navOpen tracks the off-canvas
  // sidebar. Wide layout ignores navOpen entirely (the sidebar is always shown).
  const isNarrow = useMediaQuery(SIDEBAR_BREAKPOINT);
  const [navOpen, setNavOpen] = useState(false);

  // MER-208: page_view per route change inside the customer dashboard
  // only (/admin is staff surface and deliberately untracked).
  useEffect(() => {
    track("page_view", { path: location.pathname });
  }, [location.pathname]);

  // MER-528: a route change (tab click) closes the drawer so the user lands on
  // the content, not a covered page. No-op in wide layout.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // MER-528: Escape closes the open drawer (standard dismissible-overlay UX).
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  // MER-688: staff (impersonating or not) see every manifest tab plus the
  // Internal block; a client sees the stripped nav. Same session read as
  // StaffGate; the manifest is public so this is a UI gate, not a data gate.
  const { session } = useAuth();
  const { sections, internalTabs, showInternal } = buildNavSections(manifest.tabs, {
    isStaff: isStaff(session),
  });

  // MER-674: the Executive board page is not a manifest tab. Its link exists
  // only when the tenant has exec_kpi rows (one probe per tenant, cached in
  // ExecKpiProvider); a tenant without the workbook never sees the entry.
  // MER-690: it renders as the first entry of the "Dashboard Pro+" section.
  // AJ 2026-09-28: a manifest tab a customer override marked `executive: true`
  // (Best Disposal's Board, labelled Executive) IS that tenant's Executive
  // entry, so the workbook link is not rendered beside it: one Executive, never
  // two, whatever the exec_kpi probe later says.
  const execTab = executiveTabId(manifest.tabs, { isStaff: isStaff(session) });
  const workbookAvailable = useExecKpiAvailability() === "available";
  // AJ 2026-09-28: `executive_page: derived` (Best Disposal) gives a tenant
  // with no workbook the Executive link to the Meridian-derived board.
  const derivedExecutive = manifest.executive_page === "derived";
  const execAvailable = (workbookAvailable || derivedExecutive) && !execTab;

  // AJ 2026-09-30: the sidebar badge reads the same sync_log entity as the
  // "Updated @" chip on the route on screen, so the two never disagree.
  const routeSegment = routeTabSegment(location.pathname, slug);
  const freshnessEntity = entityForTabRoute(manifest, routeSegment);
  // AJ 2026-10-01: report chunks load in the background once the page is up.
  usePrefetchReports(manifest.tabs);

  // Issue #1096: the Help button and My requests. Off unless the tenant's
  // config.yaml sets help_desk.enabled; staff always see it so it can be
  // tried on the preview site before any customer does.
  const showHelp = helpDeskVisible(manifest, { isStaff: isStaff(session) });
  const helpStaffPreview = showHelp && !helpDeskEnabled(manifest);
  const helpButton =
    showHelp && slug ? (
      <HelpButton slug={slug} pageLabel={routeSegment} staffPreview={helpStaffPreview} />
    ) : null;

  // MER-528: single column in drawer mode (the sidebar floats out of flow as a
  // fixed off-canvas panel); the fixed 240px column only exists when wide.
  const containerStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "240px 1fr",
    minHeight: "100vh",
  };

  const asideBase: CSSProperties = {
    background: "var(--panel)",
    borderRight: "1px solid var(--border)",
    padding: "16px 0",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    overflowY: "auto",
  };

  const asideStyle: CSSProperties = isNarrow
    ? {
        ...asideBase,
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 40,
        width: 264,
        maxWidth: "85vw",
        height: "100vh",
        transform: navOpen ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.2s ease",
        boxShadow: navOpen ? "2px 0 16px rgba(0,0,0,0.18)" : "none",
      }
    : { ...asideBase, position: "sticky", top: 0, height: "100vh" };

  return (
    <>
      <DemoBanner />
      <div style={containerStyle}>
        {/* MER-528: drawer-mode top bar carrying the hamburger toggle. Only
            rendered below the breakpoint; the wide layout shows the sidebar. */}
        {isNarrow ? (
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 30,
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 14px",
              background: "var(--panel)",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <button
              type="button"
              aria-label="Toggle navigation"
              aria-expanded={navOpen}
              onClick={() => setNavOpen((open) => !open)}
              style={{
                font: "inherit",
                fontSize: 20,
                lineHeight: 1,
                padding: "4px 10px",
                border: "1px solid var(--border)",
                borderRadius: 8,
                background: "transparent",
                color: "var(--text)",
                cursor: "pointer",
              }}
            >
              ☰
            </button>
            <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>
              {manifest.customer_name ?? slug}
            </span>
            {helpButton ? <span style={{ marginLeft: "auto" }}>{helpButton}</span> : null}
          </div>
        ) : null}
        {/* MER-528: tap-away backdrop while the drawer is open. */}
        {isNarrow && navOpen ? (
          <div
            aria-hidden
            onClick={() => setNavOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 35, background: "rgba(0,0,0,0.4)" }}
          />
        ) : null}
        <aside style={asideStyle}>
          <header style={{ padding: "0 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <img
                src="/brand/meridian-mark.png"
                srcSet="/brand/meridian-mark.png 1x, /brand/meridian-mark-2x.png 2x"
                alt="Meridian"
                width={32}
                height={32}
                style={{ flexShrink: 0, display: "block" }}
              />
              <div style={{ lineHeight: 1.05, fontWeight: 800, letterSpacing: 0.3 }}>
                <div style={{ fontSize: 13, color: "var(--text)" }}>MERIDIAN ANALYTICS</div>
                <div style={{ fontSize: 9, color: "var(--text-2)", fontWeight: 500 }}>
                  Operations Intelligence
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: 16,
                fontWeight: 700,
                fontSize: 16,
                color: "var(--text)",
                letterSpacing: -0.2,
              }}
            >
              {manifest.customer_name ?? slug}
            </div>
            <div
              style={{
                marginTop: 8,
                display: "inline-block",
                fontSize: 10,
                padding: "4px 10px",
                borderRadius: 12,
                background:
                  "linear-gradient(95deg, rgba(15,107,92,0.08) 0%, rgba(38,168,159,0.08) 100%)",
                border: "1px solid var(--brand-line)",
                color: "var(--brand-deep)",
                textTransform: "uppercase",
                letterSpacing: 0.4,
                fontWeight: 700,
              }}
            >
              {(manifest.tier ?? "growth").toString()}
            </div>
          </header>

          {/* MER-528: any nav activation (tab link, upload CTA, sign out)
              closes the drawer in narrow mode; no-op when wide. */}
          <nav
            onClick={isNarrow ? () => setNavOpen(false) : undefined}
            style={{ display: "grid", gap: 0, flex: 1, overflowY: "auto", minHeight: 0 }}
          >
            {/* MER-690: one "Dashboard Pro+" section. The Executive link
                (MER-674, not a manifest tab) is its first entry whenever the
                probe says the tenant has the workbook. The header renders
                when the link or any tab is present, so a section with
                nothing in it renders no header either (the old shell
                suppressed an empty Pro+ header the same way). */}
            {sections.map((section, index) => {
              const withExec = index === 0 && execAvailable;
              return withExec || section.tabs.length > 0 ? (
                <Fragment key={section.title}>
                  <div style={SECTION_HEADER_STYLE}>{section.title}</div>
                  {withExec ? (
                    <NavLink
                      to={`/${slug}/Executive`}
                      style={navLinkStyle}
                      onClick={() =>
                        track("tab_change", { tab_id: "Executive", tab_name: "Executive" })
                      }
                    >
                      <span>Executive</span>
                    </NavLink>
                  ) : null}
                  {section.tabs.map((tab) => (
                    <ManifestTabLink
                      key={tab.id ?? tab.label}
                      tab={tab}
                      slug={slug}
                      customerTier={manifest.tier}
                    />
                  ))}
                </Fragment>
              ) : null;
            })}

            {/* MER-688 / MER-690: Insights + Findings are Meridian-internal
                surfaces, and the staff_only manifest tabs list here after
                them (same ManifestTabLink: lock badge, StaffOnlyNotice on the
                page). Only staff get the block at all. */}
            {showInternal ? (
              <>
                <div style={{ ...SECTION_HEADER_STYLE, marginTop: 8 }}>Internal</div>
                <NavLink to={`/${slug}/Insights`} style={navLinkStyle}>
                  <span>Insights</span>
                </NavLink>
                <NavLink to={`/${slug}/Findings`} style={navLinkStyle}>
                  <span>Findings</span>
                </NavLink>
                {/* MER-706: the staff variance register, beside Findings. */}
                <NavLink to={`/${slug}/Variance`} style={navLinkStyle}>
                  <span>Variance Register</span>
                </NavLink>
                {internalTabs.map((tab) => (
                  <ManifestTabLink
                    key={tab.id ?? tab.label}
                    tab={tab}
                    slug={slug}
                    customerTier={manifest.tier}
                  />
                ))}
              </>
            ) : null}

            <div style={{ ...SECTION_HEADER_STYLE, marginTop: 8 }}>Account</div>
            <NavLink to={`/${slug}/Settings`} style={navLinkStyle}>
              <span>Settings</span>
            </NavLink>
            {showHelp ? (
              <NavLink to={`/${slug}/Help`} style={navLinkStyle}>
                <span>My requests</span>
              </NavLink>
            ) : null}
            {/* MER-182: external CTA to the per-customer upload portal.
              Same slug pattern as the dashboard route. New tab so the
              user keeps the dashboard open while uploading. */}
            {slug ? (
              <a
                href={`https://upload.meridiananalytics.io/${slug}`}
                target="_blank"
                rel="noopener noreferrer"
                style={externalNavLinkStyle}
              >
                <span>Upload Data</span>
                <span aria-hidden style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  ↗
                </span>
              </a>
            ) : null}
            <button
              type="button"
              onClick={handleSignOut}
              style={{
                font: "inherit",
                fontSize: 13,
                padding: "9px 16px",
                border: "none",
                borderLeft: "3px solid transparent",
                borderRadius: 0,
                background: "transparent",
                color: "var(--text)",
                cursor: "pointer",
                textAlign: "left",
                fontWeight: 400,
              }}
            >
              Sign out
            </button>
          </nav>

          {/* A tab with no feed (null, DOT Audit) or a non-tab route
              (undefined) falls back to the tenant's newest sync. */}
          <FreshnessBadge entityKey={freshnessEntity ?? null} />
        </aside>

        <section style={{ minWidth: 0 }}>
          {/* Issue #1096: the top bar carrying Help in the wide layout (the
              narrow layout carries it in the hamburger bar above). */}
          {helpButton && !isNarrow ? (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "10px 28px 0",
              }}
            >
              {helpButton}
            </div>
          ) : null}
          {/* AJ 2026-10-01: a page that throws shows a card with Retry here, the menu stays up. */}
          <ErrorBoundary scope="page" resetKey={location.pathname} label={routeSegment}>
            {children}
          </ErrorBoundary>
        </section>
      </div>
    </>
  );
}
